import React, { memo, useCallback, useMemo } from 'react';
import * as R from 'ramda';
import { useSelector } from 'react-redux';
import { includes } from 'lodash';
import PropTypes from 'prop-types';
import {
  activeToOptions,
  CustomAutocomplete,
  defaultAutoCompleteProps,
  renderInput,
  renderTags
} from '@utils/autocomplete-utils';
import './input-search.scss';

const InputSearch = ({
  ariaLabel,
  autocompleteContainerStyles,
  avatarProps,
  chipSize,
  containerStyles,
  deleteIcon,
  disabled,
  errors,
  fieldName,
  filterFunc,
  label,
  limit,
  onChange,
  onInputChange,
  options,
  placeHolder,
  preserveUnresolvedSelections,
  readOnlyEntries,
  squareChips,
  storePath,
  renderOption,
  textReadOnly,
  toOptions,
  values,
  variant
}) => {
  const data = useSelector(state => R.path(storePath || [], state));

  const optionList = useMemo(() => {
    if (options) {
      return options.map(option => ({ title: option.name, value: option.id }));
    }
    const convertOptionsFunc = toOptions ? toOptions : activeToOptions;
    return convertOptionsFunc(data, filterFunc);
  }, [data, filterFunc, options, toOptions]);

  const id = useMemo(() => `${fieldName}-input-search-textfield`, [fieldName]);

  // Selected values the viewer can't resolve via optionList (e.g. a
  // cross-agency owner not present in the agency-scoped redux user list).
  const hiddenValues = useMemo(() => {
    const visibleIds = new Set(optionList?.map(option => option.value));
    return (values || []).filter(value => !visibleIds.has(value));
  }, [optionList, values]);

  // Build read-only chips for hidden values, using pre-fetched entity data
  // (readOnlyEntries) instead of the redux user list, so they're always
  // visible but can't be selected/removed through this field.
  const readOnlyValues = useMemo(() => {
    if (!readOnlyEntries) {
      return [];
    }
    return hiddenValues
      .map(value => readOnlyEntries.find(entry => entry.id === value))
      .filter(Boolean)
      .map(entry => ({
        title: `${entry.name}${entry.email ? ` (${entry.email})` : ''}`,
        value: entry.id,
        readOnly: true
      }));
  }, [hiddenValues, readOnlyEntries]);

  // Return the options that match the selected values, plus any read-only chips:
  const defaultValues = useMemo(
    () => [...(optionList?.filter(option => includes(values, option.value)) || []), ...readOnlyValues],
    [optionList, readOnlyValues, values]
  );

  const handleChange = useCallback((event, newValues) => {
    // If a limit was set, isolate just the newly-selected value (if any),
    // replacing whatever resolvable option was already selected:
    const resolvedValues = limit === 1
      ? newValues.filter(val => !defaultValues.includes(val))
      : newValues;

    if (!preserveUnresolvedSelections) {
      onChange(event, resolvedValues);
      return;
    }

    // Preserve any previously selected id the current viewer can't resolve
    // (e.g. a cross-agency owner not present in optionList) instead of
    // silently dropping it whenever this field is edited. Individually
    // removing a chip already keeps any other hidden value intact (it was
    // part of the rendered value and wasn't the one removed), so only add
    // back ones missing entirely (e.g. after the field's "clear all", or
    // a single-select field's replace action).
    const resolvedIds = new Set(resolvedValues.map(value => value.value));
    const missingHidden = hiddenValues.filter(value => !resolvedIds.has(value));
    onChange(event, [...resolvedValues, ...missingHidden.map(value => ({ value }))]);
  }, [defaultValues, hiddenValues, limit, onChange, preserveUnresolvedSelections]);

  const autoCompleteProps = useMemo(() => {
    const props = { ...defaultAutoCompleteProps };
    if (renderOption) {
      props.renderOption = renderOption;
    }
    return props;
  }, [renderOption]);

  if (!optionList) {
    return null;
  }

  return (
    <div styleName="input-search-container" style={{...containerStyles}}>
      <div styleName="input-search-autocomplete-container" style={{...autocompleteContainerStyles}}>
        <CustomAutocomplete
          {...autoCompleteProps}
          disabled={disabled}
          id={id}
          options={optionList}
          defaultValue={defaultValues}
          value={defaultValues}
          onChange={handleChange}
          onInputChange={onInputChange}
          renderTags={renderTags({
            avatarProps,
            chipSize,
            deleteIcon,
            disabled,
            squareChips
          })}
          renderInput={renderInput(errors, label, placeHolder, variant, textReadOnly, ariaLabel)}
        />
      </div>
    </div>
  );
};

InputSearch.propTypes = {
  ariaLabel: PropTypes.string,
  autocompleteContainerStyles: PropTypes.object,
  avatarProps: PropTypes.object,
  chipSize: PropTypes.string,
  containerStyles: PropTypes.object,
  deleteIcon: PropTypes.node,
  disabled: PropTypes.bool,
  errors: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  fieldName: PropTypes.string,
  filterFunc: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  limit: PropTypes.number,
  onChange: PropTypes.func,
  onInputChange: PropTypes.func,
  options: PropTypes.array,
  placeHolder: PropTypes.string,
  preserveUnresolvedSelections: PropTypes.bool,
  readOnlyEntries: PropTypes.array,
  renderOption: PropTypes.func,
  squareChips: PropTypes.bool,
  storePath: PropTypes.array,
  textReadOnly: PropTypes.bool,
  toOptions: PropTypes.func,
  values: PropTypes.any,  // eslint-disable-line react/forbid-prop-types
  variant: PropTypes.string
};

InputSearch.defaultProps = {
  autocompleteContainerStyles: {
    maxWidth: '16rem'
  },
  variant: 'standard'
};

export default memo(InputSearch);
